import { useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import { useStore } from 'hooks';
import { getFullName } from 'utils';
import { Button, ISelectValue } from 'components/ui';
import { StyledSelect, Wrapper } from './third-party-accounts-select.styled';
import { newCustomStyles } from '../../../../selects';

interface IProps {
  collapsed: boolean;
  card?: any;
}

export const ThirdPartyAccountsSelect = observer(
  ({ collapsed, card }: IProps) => {
    const { createRequest, telephony } = useStore();
    const { setAddNewThirdPartyModalProperties, initialPhoneNumber } =
      createRequest;

    const callerInfo = card ? card.callerInfo : telephony.callerInfo;
    const {
      loading,
      thirdPartyType,
      allOtherProviders,
      allOtherEmployees,
      allOtherPersons,
      organizationId,
      legalId,
      physicalId,
      setSelectedId,
      getOtherEmployeeAccounts,
    } = createRequest.applicant;

    const organizationsOptions: ISelectValue[] = useMemo((): ISelectValue[] => {
      return allOtherProviders.map((item) => {
        return {
          label: item.name,
          value: item._id,
        };
      });
    }, [allOtherProviders.length]);

    const handleChangeOrganization = async (id: string): Promise<void> => {
      if (id !== organizationId) {
        setSelectedId('organizationId', id);
        setSelectedId('legalId', '');
        await getOtherEmployeeAccounts(id, initialPhoneNumber);
      }
    };

    const selectedOrganization = useMemo(() => {
      return (
        organizationsOptions.find(
          (item: { [key: string]: any }): boolean =>
            item.value === organizationId
        ) || ''
      );
    }, [organizationId, thirdPartyType]);

    const mapItemsToSelectValues = (items: any[]): ISelectValue[] => {
      return items.map((item) => {
        return {
          label: getFullName(item),
          value: item._id,
        };
      });
    };

    const prepareOrganizations = (): ISelectValue[] => {
      if (thirdPartyType === 'other_employee') {
        const employees = allOtherEmployees.filter((employee) => {
          return employee.other_provider_id === organizationId;
        });

        return mapItemsToSelectValues(employees);
      }

      if (thirdPartyType === 'other_person') {
        return mapItemsToSelectValues(allOtherPersons);
      }

      return [];
    };

    const thirdPartyAccounts: ISelectValue[] = useMemo(() => {
      return prepareOrganizations();
    }, [
      thirdPartyType,
      getSnapshot(allOtherEmployees),
      getSnapshot(allOtherPersons),
      organizationId,
      createRequest.loading.addNewOtherEmployee.fulfilled,
      createRequest.loading.addNewOtherPerson.fulfilled,
    ]);

    const selectedAccount = useMemo(() => {
      return (
        thirdPartyAccounts.find(
          (item: { [key: string]: any }): boolean =>
            item.value ===
            (thirdPartyType === 'other_employee' ? legalId : physicalId)
        ) || ''
      );
    }, [
      allOtherEmployees,
      allOtherPersons,
      thirdPartyAccounts.length,
      legalId,
      physicalId,
      thirdPartyType,
    ]);

    const handleChangeAccount = (id: string): void => {
      if (!selectedAccount || id !== selectedAccount?.value) {
        if (thirdPartyType === 'other_employee') {
          if (!!selectedOrganization) {
            const currentLegal = cloneDeep(
              allOtherEmployees.find((item: any) => item._id === id)!
            );
            callerInfo.setCallerInfo(
              getFullName(currentLegal),
              currentLegal.email || '',
              currentLegal.phone_numbers[0]?.number ||
                createRequest.initialPhoneNumber,
              '',
              ''
            );
            setSelectedId('legalId', id);
          }
        } else {
          const currentPhysical = cloneDeep(
            allOtherPersons.find((item: any): boolean => item._id === id)!
          );

          callerInfo.setCallerInfo(
            getFullName(currentPhysical),
            currentPhysical.email || '',
            currentPhysical.phone_numbers[0]?.number || callerInfo.first_number,
            '',
            ''
          );
          setSelectedId('physicalId', id);
        }
      }
    };

    const addNewOrganization = (): void => {
      setAddNewThirdPartyModalProperties('addOrganization', true);
      setAddNewThirdPartyModalProperties('show', true);
    };

    return (
      <>
        {thirdPartyType === 'other_employee' && (
          <Wrapper collapsed={collapsed}>
            <StyledSelect
              isDisabled={!loading.otherProviderAccounts.fulfilled}
              collapsed={collapsed}
              styles={newCustomStyles}
              options={organizationsOptions}
              placeholder={'Название организации'}
              menuPortalTarget={document.body}
              closeMenuOnScroll={true}
              closeMenuOnSelect={true}
              className={'other'}
              classNamePrefix={'other'}
              menuPlacement={'auto'}
              isMulti={false}
              value={selectedOrganization}
              onChange={(newValue: any) =>
                handleChangeOrganization(newValue.value)
              }
            />

            <Button
              mode={'neutral'}
              variant={'outline'}
              onClick={addNewOrganization}
              style={{ flexShrink: '0' }}
            >
              Новая организация
            </Button>
          </Wrapper>
        )}

        {(thirdPartyType === 'other_person' ||
          (thirdPartyType === 'other_employee' && !!organizationId)) && (
          <StyledSelect
            isDisabled={
              (!loading.otherEmployeeAccounts.fulfilled &&
                !loading.otherPersonAccounts.fulfilled) ||
              thirdPartyAccounts.length === 0
            }
            collapsed={collapsed}
            styles={newCustomStyles}
            options={thirdPartyAccounts}
            placeholder={
              thirdPartyType === 'other_person'
                ? 'ФИО частного лица'
                : 'ФИО представителя организации'
            }
            menuPortalTarget={document.body}
            closeMenuOnScroll={true}
            closeMenuOnSelect={true}
            className={'other'}
            classNamePrefix={'other'}
            menuPlacement={'auto'}
            isMulti={false}
            value={selectedAccount}
            onChange={(newValue: any) => handleChangeAccount(newValue.value)}
          />
        )}
      </>
    );
  }
);
